import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'progress';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  progress?: { current: number; total: number };
}

interface ToastContextType {
  addToast: (type: ToastType, message: string, progress?: { current: number; total: number }) => string;
  updateToast: (id: string, updates: Partial<Pick<Toast, 'message' | 'type' | 'progress'>>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const scheduleAutoDismiss = useCallback((id: string, type: ToastType) => {
    // Clear existing timer if any
    if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
    // No auto-dismiss for progress
    if (type === 'progress') return;
    const delay = type === 'success' || type === 'info' ? 5000 : 8000;
    timersRef.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete timersRef.current[id];
    }, delay);
  }, []);

  const addToast = useCallback((type: ToastType, message: string, progress?: { current: number; total: number }): string => {
    const id = `toast-${++nextId}`;
    setToasts(prev => [...prev, { id, type, message, progress }]);
    scheduleAutoDismiss(id, type);
    return id;
  }, [scheduleAutoDismiss]);

  const updateToast = useCallback((id: string, updates: Partial<Pick<Toast, 'message' | 'type' | 'progress'>>) => {
    setToasts(prev => prev.map(t => {
      if (t.id !== id) return t;
      const updated = { ...t, ...updates };
      // Reschedule auto-dismiss if type changed
      if (updates.type && updates.type !== t.type) {
        scheduleAutoDismiss(id, updates.type);
      }
      return updated;
    }));
  }, [scheduleAutoDismiss]);

  const removeToast = useCallback((id: string) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, updateToast, removeToast }}>
      {children}
      {/* Toast Container - fixed bottom-right, above modals */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[80] flex flex-col gap-2 max-w-sm">
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border shadow-lg text-sm animate-slide-in-right ${
                toast.type === 'success' ? 'bg-green-900/90 border-green-700/50 text-green-200' :
                toast.type === 'error' ? 'bg-red-900/90 border-red-700/50 text-red-200' :
                toast.type === 'warning' ? 'bg-yellow-900/90 border-yellow-700/50 text-yellow-200' :
                toast.type === 'progress' ? 'bg-blue-900/90 border-blue-700/50 text-blue-200' :
                'bg-slate-800/90 border-slate-600/50 text-slate-200'
              }`}
            >
              {/* Icon */}
              <span className="mt-0.5 shrink-0">
                {toast.type === 'success' && (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                )}
                {toast.type === 'error' && (
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                )}
                {toast.type === 'warning' && (
                  <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                )}
                {toast.type === 'progress' && (
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
                )}
                {toast.type === 'info' && (
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                )}
              </span>
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-xs leading-relaxed">{toast.message}</div>
                {toast.type === 'progress' && toast.progress && (
                  <div className="mt-1.5">
                    <div className="w-full h-1 bg-blue-950 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.round((toast.progress.current / toast.progress.total) * 100)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-blue-400 mt-0.5">
                      {toast.progress.current} of {toast.progress.total}
                    </div>
                  </div>
                )}
              </div>
              {/* Dismiss */}
              <button
                onClick={() => removeToast(toast.id)}
                className="shrink-0 p-0.5 rounded hover:bg-white/10 transition-colors"
              >
                <svg className="w-3.5 h-3.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
